import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Pagination from '@navi/web-ui/lib/components/Pagination/Pagination';
import GridRow from '@navi/web-ui/lib/layouts/Grid/GridRow/GridRow';
import Typography from '@navi/web-ui/lib/primitives/Typography/Typography';
import SuspenseLoader from 'src/components/SkeletonLoader/SuspenseLoader';
import TableLoader from 'src/components/TableLoader/TableLoader';
import { RootState } from 'src/store';
import { createKey } from 'src/utils/CaseDetail.utils';
import { isObjectEmpty } from 'src/utils/commonUtils';
import { createQueryParams, readQueryParams } from 'src/utils/QueryParamsHelper';
import { fetchCallInteractions } from '../../actions/interactionsActions';
import {
  CALL_INTERACTIONS,
  getInteractionsFeedbackColumns,
  getLastMonthDate,
  getTodayDate,
  NO_RECORD_FOUND
} from '../../constants/communicationHistory.constant';
import { QueryParams, RowClickEvent } from '../../../../components/interfaces';
import { DropDownPosition } from '@navi/web-ui/lib/components/Pagination/constant';
import { DateFormat, incrementDateByMins, utcToIST } from 'src/utils/DateHelper';
import { defaultPageSize } from '../../../Cases/constants/CasesConstants';
import { TAB_KEYS } from '../../constants';
import {
  FROM_TIME,
  PREVIOUS_LOAN_ACCOUNT_INTERACTIONS,
  TO_TIME
} from '../PreviousLoanCommunicationHistory/constants';
import { addClickstreamEvent } from '../../../../service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '../../../../service/clickStream.constant';
import { ColDef } from 'ag-grid-community';
import {
  ADD_TIME,
  defaultFeedbackTypes,
  defaultPreviousLoanInteractionsFromDateString,
  FeedbackTypeFilterName,
  feedbackTypes,
  RECORDING_ROW_HEIGHT
} from './constants';
import { SelectPickerOptionProps } from '@navi/web-ui/lib/components/SelectPicker/types';
import { setFeedbackFilters } from 'src/reducers/commonSlice';
import FilterIcon from 'src/assets/images/icons/FilterIcon';
import AmeyoNonConnectedDispositions from './AmeyoNonConnectedDispositions';
import { setInteractionRowDetailedView } from '../../reducers/caseDetailSlice';
import { assignIndexToReallocationList } from 'src/pages/Reallocation/reducers/reallocationSlice';
import Filter from '@cp/src/components/Filter';
import DateTimePickerComponent from '../../../../components/DateTimePicker/DateTimePickerComponent';
import { DATE_TIME_TYPE } from '../../../../components/DateTimePicker/constants';
import styles from './index.module.scss';
import Switch from '@cp/src/components/Switch/Switch';
import dayjs from 'dayjs';
import cx from 'classnames';

const AgTable = React.lazy(
  () => import(/* webpackChunkName: ['table'] */ '@navi/web-ui/lib/components/AgTable/AgTable')
);

const CallInteractions = () => {
  const {
    loanId = '',
    customerId = '',
    selectedTab = '',
    caseReferenceId = '',
    caseBusinessVertical = '',
    previousCaseReferenceId = ''
  } = useParams();
  const isPreviousLoanInteraction = selectedTab === TAB_KEYS.PREV_LOAN_INTERACTIONS;
  const maxInputDate = incrementDateByMins(utcToIST(getTodayDate()), 1).toISOString().slice(0, 16);
  const defaultLastMonthDate = utcToIST(getLastMonthDate()).toISOString().slice(0, 16);
  const defaultPreviousLoanInteractionsFromDate = dayjs(
    defaultPreviousLoanInteractionsFromDateString,
    DateFormat.ISO_DATE_FORMAT
  ).format(DateFormat.COMMUNICATION_HISTORY_DATE_FORMAT);
  const pageData = useSelector(
    (state: RootState) => state.caseDetail.pageData?.[createKey(loanId, customerId)]
  );
  const token = useSelector((store: RootState) => store?.common?.userData.token);
  const roles = useSelector((store: RootState) => store?.common?.userData.roles);
  const user = useSelector((state: RootState) => state.common.userData);
  const feedbackFilters = useSelector((state: RootState) => state.common.feedbackFilters);
  const { data: callInteractionsData, loading: callInteractionsLoading } =
    pageData?.callInteractions || {};

  const [searchParams] = useSearchParams();
  const {
    [CALL_INTERACTIONS]: queryParams,
    [PREVIOUS_LOAN_ACCOUNT_INTERACTIONS]: previousLoanAccountParams,
    ...rest
  } = readQueryParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const createCommonPayload = (loanAccount: string, caseReferenceId: string) => {
    return {
      page_no: queryParams?.page || 0,
      page_size: queryParams?.size || defaultPageSize,
      types: feedbackFilters ? feedbackFilters : defaultFeedbackTypes,
      loan_account_number: loanAccount,
      customer_reachable: queryParams?.contactable,
      from: defaultPreviousLoanInteractionsFromDate,
      caseReferenceId: caseReferenceId,
      caseBusinessVertical: caseBusinessVertical
    };
  };

  const createPayloadForCallInteractions = () => {
    if (isPreviousLoanInteraction) {
      return createCommonPayload(
        previousLoanAccountParams?.previousLoanAccount,
        previousLoanAccountParams?.previousCaseReferenceId
      );
    }
    return {
      ...createCommonPayload(loanId, caseReferenceId),
      from: queryParams?.[FROM_TIME] || defaultLastMonthDate,
      to: queryParams?.[TO_TIME] || maxInputDate,
      role: roles?.join()
    };
  };

  const getCallInteractionsData = () => {
    const payload = createPayloadForCallInteractions();
    if (payload.loan_account_number)
      dispatch(fetchCallInteractions(loanId, customerId, payload, isPreviousLoanInteraction));
  };
  useEffect(() => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Interactions_CallFeedbackPageLand);
    return () => {
      dispatch(setFeedbackFilters(''));
    };
  }, []);

  useEffect(() => {
    if (!pageData?.waInteractions && !isPreviousLoanInteraction) {
      getCallInteractionsData();
    }
  }, [pageData?.waInteractions]);

  useEffect(() => {
    if (feedbackFilters) {
      getCallInteractionsData();
    }
  }, [feedbackFilters]);

  useEffect(() => {
    if (!isObjectEmpty(queryParams)) {
      getCallInteractionsData();
    } else if (isPreviousLoanInteraction) {
      getCallInteractionsData();
    }
  }, [searchParams]);

  const updateQueryParamsAndNavigate = (params?: QueryParams, lastUsed?: string) => {
    const url = createQueryParams({
      [CALL_INTERACTIONS]: { ...queryParams, ...params, lastUsed },
      [PREVIOUS_LOAN_ACCOUNT_INTERACTIONS]: {
        ...previousLoanAccountParams
      },
      ...rest
    });
    navigate(url, {
      replace: true
    });
  };

  const getCurrentPageNumber = (): number => {
    return Number(queryParams?.page) + 1 || 1;
  };

  const getCurrentPageSize = (): number => {
    return Number(queryParams?.size) || defaultPageSize;
  };

  const handlePageSizeChange = (size: number) =>
    updateQueryParamsAndNavigate({ size: size.toString(), page: 0 });

  const handlePageChange = (page: number) => {
    updateQueryParamsAndNavigate({ page: (page - 1).toString() });
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Interactions_PaginationClick, { tab: 'Call' });
  };

  const handleTimeChange = (val: React.ChangeEvent<HTMLInputElement>, filterKey: string) => {
    updateQueryParamsAndNavigate({
      [filterKey]: val.target.value
    });
  };

  const handleTimeChangeClear = (val: React.ChangeEvent<HTMLInputElement>, filterKey: string) => {
    val.target.value = disbursmentDate;
    updateQueryParamsAndNavigate({
      [filterKey]: val.target.value
    });
  };

  const handleOnFromDateChange = (val: React.ChangeEvent<HTMLInputElement>) => {
    if (val.target.value === '') {
      handleTimeChangeClear(val, FROM_TIME);
    } else {
      handleTimeChange(val, FROM_TIME);
    }
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Interactions_FromDateIcon, {
      tab: 'Call'
    });
  };
  const handleOnToDateChange = (val: React.ChangeEvent<HTMLInputElement>) => {
    handleTimeChange(val, TO_TIME);
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Interactions_ToDateIcon, {
      tab: 'Call'
    });
  };

  const handleContactableSwitchChange = () => {
    const isChecked = queryParams?.contactable === 'true';
    updateQueryParamsAndNavigate({ contactable: (!isChecked).toString(), page: 0 });
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Interactions_OnlyContactableToggle, {
      tab: 'Call'
    });
  };

  const { data: caseDetails } = pageData?.details || {};
  const { startDate } = caseDetails || {};
  const disbursmentDate = startDate + ADD_TIME;

  const [isDownloading, setIsDownloading] = useState(false);

  const handlerFilterChange = (val: SelectPickerOptionProps, key: string) => {
    const filterValues = Array.isArray(val) && val.map(item => item.value);
    const formattedFilterValue = filterValues.join(',');
    dispatch(
      setFeedbackFilters(formattedFilterValue ? formattedFilterValue : defaultFeedbackTypes)
    );
  };

  const interactionRowClicked = (e: RowClickEvent) => {
    // disable click on detailed rows or in progress rows and when nonConnectedData is not present
    if (e.data?.isDetailRow || e.data?.ameyoNonConnectedInteractionsInfo.length < 2) {
      return;
    }
    const data = [...(callInteractionsData?.data || [])];
    const clickedData = e.data;

    let clickedIndex = data.findIndex(item => {
      return clickedData.referenceId === item.referenceId;
    });

    let newData;

    // If next row is detail row, remove it
    if (data[clickedIndex + 1]?.isDetailRow) {
      data.splice(clickedIndex + 1, 1);
      data[clickedIndex] = { ...data[clickedIndex], toggleState: false };
      newData = assignIndexToReallocationList(data);
      dispatch(
        setInteractionRowDetailedView({
          lan: loanId,
          customerRefrenceId: customerId,
          data: newData,
          pages: callInteractionsData?.pages
        })
      );
      return;
    }

    // find detail row index
    const detailRowIndex = data.findIndex(item => {
      return item?.isDetailRow;
    });

    // If detail row exists, remove it
    if (detailRowIndex !== -1) {
      data[detailRowIndex - 1] = { ...data[detailRowIndex - 1], toggleState: false };
      data.splice(detailRowIndex, 1);
      if (detailRowIndex < clickedIndex) {
        clickedIndex -= 1;
      }
    }

    // New detail row to be appended after the clicked row
    const newRow = {
      ...clickedData,
      isDetailRow: true,
      rowIndex: clickedIndex + 1
    };
    data.splice(clickedIndex + 1, 0, newRow);
    data[clickedIndex] = { ...data[clickedIndex], toggleState: true };
    newData = assignIndexToReallocationList(data);

    dispatch(
      setInteractionRowDetailedView({
        lan: loanId,
        customerRefrenceId: customerId,
        data: newData,
        pages: callInteractionsData?.pages
      })
    );
  };

  const getRowHeight = (params: any) => {
    const distributionList = params?.data?.ameyoNonConnectedInteractionsInfo;
    const distributionCount = distributionList?.length - 1;
    if (params?.data?.isDetailRow) {
      if (distributionCount > 1) {
        //height for single distribution row
        return distributionCount * 60;
      }
      //default height for detail row
      return 55;
    }
    if (params.data?.metadata?.recordingUrl) {
      return RECORDING_ROW_HEIGHT;
    }
    return 56;
  };

  const getAgRowStyle = (params: any) => {
    return {
      borderBottom: params.data.toggleState ? 'none' : '',
      backgroundColor: params.data.toggleState ? '#dbebff' : '#ffffff'
    };
  };

  const getRowId = (params: any) => {
    if (params.data.isDetailRow) {
      return params.data.rowIndex;
    }
    return params.data?.referenceId || params.data?.agentReferenceId;
  };

  return (
    <div className={styles.systemInteractionsWrapper}>
      <div className={styles.systemInteractionsTopContainer}>
        <GridRow
          className={cx(
            styles.w100,
            styles.interactionsFlexWrapper,
            styles.commonInteractionsWrapper,
            isPreviousLoanInteraction && 'justify-end'
          )}
        >
          {!isPreviousLoanInteraction ? (
            <>
              <div className={styles.dateFilterWrapper}>
                <DateTimePickerComponent
                  heading="From"
                  type={DATE_TIME_TYPE.DATE_TIME}
                  id={FROM_TIME}
                  name={FROM_TIME}
                  max={queryParams?.[TO_TIME] || maxInputDate}
                  value={queryParams?.[FROM_TIME] || defaultLastMonthDate}
                  onChange={handleOnFromDateChange}
                />

                <DateTimePickerComponent
                  heading="To"
                  type={DATE_TIME_TYPE.DATE_TIME}
                  id={TO_TIME}
                  name={TO_TIME}
                  max={maxInputDate}
                  min={queryParams?.[FROM_TIME] || defaultLastMonthDate}
                  value={queryParams?.[TO_TIME] || maxInputDate}
                  onChange={handleOnToDateChange}
                  className={styles.datePicker}
                />

                <div className={styles.filterWrapper}>
                  <Filter
                    key={`filter-${FeedbackTypeFilterName}`}
                    title={FeedbackTypeFilterName}
                    options={feedbackTypes}
                    onSelectionChange={(val: SelectPickerOptionProps | SelectPickerOptionProps[]) =>
                      handlerFilterChange(val, FeedbackTypeFilterName)
                    }
                    filterIcon={<FilterIcon />}
                    selectedValues={feedbackFilters}
                    pickerWrapperClass={styles.pickerWrapper}
                  />
                </div>
              </div>
            </>
          ) : null}
          <div className="mr-0 mb-2">
            <GridRow className={cx(styles.justifyFlexEnd, styles.commonWrapper)}>
              <Switch
                checked={queryParams?.contactable === 'true'}
                onChange={handleContactableSwitchChange}
                headerText="Only contactable"
              />
            </GridRow>
          </div>
        </GridRow>
      </div>
      <div className={styles.table} id="callInteractionTable">
        <SuspenseLoader
          loading={!!callInteractionsLoading}
          fallBack={<TableLoader numberOfLines={6} />}
        >
          <div>
            <AgTable
              className="customAgTableStyles"
              defaultColDef={{
                suppressMovable: true,
                wrapText: true
              }}
              gridOptions={{ suppressCellSelection: true }}
              getRowId={getRowId}
              getAgRowStyle={getAgRowStyle}
              style={{
                height: `${window?.innerHeight - (isPreviousLoanInteraction ? 468 : 385)}`
              }}
              domLayout="autoHeight"
              sizeColumnsToFit
              rowData={callInteractionsData?.data}
              columnDefs={getInteractionsFeedbackColumns(user) as unknown as ColDef<any>[]}
              theme={'alpine'}
              noRowsOverlayComponent={() => (
                <Typography variant="p4" className={styles.emptyListText}>
                  {NO_RECORD_FOUND}
                </Typography>
              )}
              paginationWrapperClasses={'paginationWrapperClasses'}
              isFullWidthRow={params => !!params.rowNode.data?.isDetailRow}
              fullwidthCellRenderer={({ node }) =>
                AmeyoNonConnectedDispositions({
                  ameyoNonConnectedInteractionsInfo: node.data?.ameyoNonConnectedInteractionsInfo,
                  recipientNumber: node.data?.source?.recipientNumber
                })
              }
              getRowHeight={getRowHeight}
              onRowClicked={interactionRowClicked}
              animateRows
              PaginationComponent={
                <Pagination
                  totalCount={callInteractionsData?.pages?.totalElements || 0}
                  onPageChange={handlePageChange}
                  currentPage={getCurrentPageNumber()}
                  onPageSizeChange={handlePageSizeChange}
                  pageSize={getCurrentPageSize()}
                  pageNumberDropDownPosition={DropDownPosition.TOP}
                />
              }
            />
          </div>
        </SuspenseLoader>
      </div>
    </div>
  );
};

export default CallInteractions;
